import {Button, Card, Checkbox, Dropdown, Flex, Input, Modal, Popover, Space} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpWideShort, faPencil, faPlus, faRotate, faTrash} from "@fortawesome/free-solid-svg-icons";
import TextArea from "antd/es/input/TextArea";
import {useParams} from "react-router-dom";
import {categories} from "./list/SettingList";
import Title from "antd/es/typography/Title";
import {
    generateSettingElementPrompt,
    generateSettingElementsPrompt,
    improveSettingElementPrompt
} from "./prompts/ElementsPrompts";
import {generateSettingComponentPrompt, improveSettingComponentPrompt} from "./prompts/ComponentsPrompts";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {DraftComponent, DraftComponentCategory, DraftElement, embeddedElements} from "../../../../../../feature/Draft";
import {aiChainPrompt, similaritySearch, unembed} from "../../../../../../ai/Prompt";
import {
    addElement,
    deleteElement,
    updateComponent,
    updateElement,
    updateElements
} from "../../../../../../feature/project/draftSlice";
import React, {useEffect} from "react";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {EmbedButton} from "../../../../../../ai/components/EmbedButton";
import {EmbeddingErrorMessage} from "../../../../../../ai/components/EmbeddingErrorMessage";

export const SettingComponentDetails = (
    props: {
        category: DraftComponentCategory,
        data: DraftComponent
    }
) => {
    const draft = useAppSelector(state => state.draftSlice.draft)
    const model = useAppSelector(state => state.draftSlice.model)
    const {projectId} = useParams()
    const [loading, setLoading] = React.useState<string | undefined>(undefined)
    const [open, setOpen] = React.useState<string | undefined>(undefined)
    const [useContext, setUseContext] = React.useState(true)
    const [prompt, setPrompt] = React.useState(categories.find(c => c.key == props.category.category)?.prompt.component.single + '\n\nКомпонент: ' + props.data.title)
    const [showEmbeddingWarning, setShowEmbeddingWarning] = React.useState(false)
    const [showElementsWarning, setShowElementsWarning] = React.useState(false)
    const [unembedding, setUnembedding] = React.useState(false)
    const [generating, setGenerating] = React.useState(false)
    const [generatingSingle, setGeneratingSingle] = React.useState(false)
    const dispatch = useAppDispatch()

    const generateComponent = async (component: DraftComponent, category: string) => {
        const categoryData = categories.find(c => c.key == category)!
        let context = await similaritySearch({
            project: projectId,
            query: component.description ?? '',
            where: {'\'type\'': 'setting'}
        })
        let data = await aiChainPrompt({

            projectId: projectId ?? "",
            prompt: generateSettingComponentPrompt({
                category: categoryData,
                genres: draft.properties.genre,
                customPrompt: categoryData.prompt.component.single + '\n\nКомпонент: ' + component.title,
                synopsis: draft.properties.synopsis,
                setting: draft.setting.description,
                context: (props.data.description ? [props.data.description] : []).concat(context?.documents).flat()
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateComponent({
            // @ts-ignore
            category: props.category.category,
            component: {
                ...props.data,
                description: data.data.description,
                title: data.data.title
            }
        }))
    }

    const improveComponent = async (component: DraftComponent, category: string) => {
        const categoryData = categories.find(c => c.key == category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: (props.data.description ?? '') + '\n' + prompt,
            where: {'type': 'setting'}
        }) : undefined

        let data = await aiChainPrompt({

            projectId: projectId ?? "",
            prompt: improveSettingComponentPrompt({
                customPrompt: prompt ?? (categoryData.prompt.component.single + '\n\nКомпонент: ' + props.data.title),
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                setting: draft.setting.description,
                category: props.category,
                component: component,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateComponent({
            // @ts-ignore
            category: props.category.category,
            component: {
                ...props.data,
                description: data.data.description,
            }
        }))
    }

    const generateElement = async (element?: DraftElement) => {
        const category = categories.find(c => c.key == props.category.category)!
        const query = element ? element.description : props.data.description
        let context = query ? await similaritySearch({
            project: projectId,
            query: query,
            where: {'type': 'setting'}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: generateSettingElementPrompt({
                category: category,
                genres: draft.properties.genre,
                customPrompt: category?.prompt.element.single + (element ? ('\n\nЭлемент: ' + element?.title) : ('\n\nКомпонент: ' + props.data.title)),
                synopsis: draft.properties.synopsis,
                component: props.data,
                setting: draft.setting.description,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        if (element) {
            dispatch(updateElement({
                // @ts-ignore
                category: props.category.category,
                componentId: props.data.id,
                element: {
                    ...element,
                    embedded: false,
                    description: data.data.description
                },
            }))
        } else {
            dispatch(addElement({
                // @ts-ignore
                category: props.category.category,
                componentId: props.data.id,
                element: {
                    ...data.data,
                    embedded: false,
                    items: [],
                    id: (props.data.elements.flat().sort((a, b) => b.id - a.id)[0]?.id ?? 0) + 1,
                },
            }))
        }
    }

    const improveElement = async (element: DraftElement) => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: element.description ?? '',
            where: {'type': 'setting'}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: improveSettingElementPrompt({
                genres: draft.properties.genre,
                customPrompt: prompt ?? (category.prompt.element.extend + '\n\nЭлемент: ' + element.title),
                synopsis: draft.properties.synopsis,
                element: element,
                component: props.data,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateElement({
            category: props.category.category,
            componentId: props.data.id,
            element: {
                ...element,
                description: data.data.description,
            }
        }))
    }

    const generateElements = async (force?: boolean) => {
        if (embeddedElements(props.data.elements) == false) {
            if (props.data.elements.length == 0 || force == true) {
                const category = categories.find(c => c.key == props.category.category)!

                let context = await similaritySearch({
                    project: projectId,
                    query: props.data.description ?? '',
                    where: {'type': 'setting'}
                })

                let data = await aiChainPrompt({
                    projectId: projectId ?? "",
                    prompt: generateSettingElementsPrompt({
                        category: category,
                        genres: draft.properties.genre,
                        synopsis: draft.properties.synopsis,
                        setting: draft.setting.description,
                        component: props.data,
                        context: (props.data.description ? [props.data.description] : []).concat(context.documents).flat()
                    }),
                    model: model,
                    type: 'json'
                })

                dispatch(updateComponent({
                    // @ts-ignore
                    category: props.category.category,
                    component: {
                        ...props.data,
                        elements: data.data.map((c: any) => ({...c, embedded: false})),
                    }
                }))
            } else {
                setShowElementsWarning(true)
            }
        } else {
            setShowEmbeddingWarning(true)
        }
    }


    const addEmptyElement = async () => {
        dispatch(addElement({
            // @ts-ignore
            category: props.category.category,
            componentId: props.data.id,
            element: {
                title: '',
                description: '',
                embedded: false,
                items: [],
                id: (props.data.elements.flat().sort((a, b) => b.id - a.id)[0]?.id ?? 0) + 1,
            }
        }))
    }

    useEffect(() => {
        setPrompt(categories.find(c => c.key == props.category.category)?.prompt.component.single + '\n\nКомпонент: ' + props.data.title)
    }, [props.data]);

    return (
        <>
            <Flex gap={10} vertical={true} style={{overflowY: 'auto', scrollbarWidth: "none"}}>
                <Card title={<Flex gap={10}>
                    <DebounceInput
                        style={{fontWeight: 'bold'}}
                        placeholder={"Component title"}
                        value={props.data.title}
                        onChange={(text) => {
                            dispatch(updateComponent({
                                category: props.category.category ?? '',
                                component: {
                                    ...props.data,
                                    title: text
                                }
                            }))
                        }}/>
                    <Popover
                        placement={'bottomRight'}
                        content={
                            <Space direction={'vertical'}>
                                <TextArea onChange={(e) => setPrompt(e.target.value)}
                                          value={prompt}
                                          autoSize={true}
                                          style={{width: 300}}
                                />
                                <Checkbox checked={useContext} onChange={(e) => {
                                    setUseContext(e.target.checked)
                                }}>Include context</Checkbox>
                                <Button
                                    type={'primary'}
                                    onClick={async () => {
                                        setLoading("component")
                                        setOpen(undefined)
                                        await improveComponent(props.data, props.category.category ?? '')
                                        setLoading(undefined)
                                    }}>Go</Button>
                            </Space>
                        }
                        title="Prompt"
                        trigger="click"
                        open={open == "component"}
                        onOpenChange={(e) => setOpen(e ? "component" : undefined)}
                    >
                        <GenButton
                            mode={'icon'}
                            hideMainAction={true}
                            icon={<IconBoltGrad/>}
                            loading={loading == 'component'}
                            open={open == undefined}
                            actions={[{
                                key: "1",
                                title: "Re-generate",
                                action: async (model: string) => {
                                    await generateComponent(props.data!, props.category.category ?? '')
                                        .catch((e) => {
                                            console.log(e)
                                        })
                                },
                                render: (key, title, action) => {
                                    return <Space><BoltIcon secondary={faRotate} size={16}/>{title}</Space>
                                }
                            },
                                {
                                    key: "2", title: "Improve", action: async (model: string) => {
                                        await improveComponent(props.data, props.category.category ?? '')
                                    }, render: (key, title, action) => {
                                        return <Flex gap={10} justify={'space-between'}>
                                            <Space><BoltIcon secondary={faArrowUpWideShort} size={16}/>{title}
                                            </Space><Button type={'text'} size={'small'}
                                                            icon={<FontAwesomeIcon icon={faPencil}/>}
                                                            onClick={(e) => {
                                                                setPrompt(categories.find(c => c.key == props.category.category)?.prompt.component.single + '\n\nКомпонент: ' + props.data.title)
                                                                e.stopPropagation()
                                                                setOpen("component")
                                                            }}/>
                                        </Flex>
                                    }
                                }]
                            }/>

                    </Popover>
                </Flex>}
                >
                    <TextAreaActions
                        style={{border: "none"}}
                        value={props.data.description}
                        autoSize={true}
                        validate={text => true}
                        onChange={(text: string) => {
                            dispatch(updateComponent({
                                // @ts-ignore
                                category: props.category.category,
                                component: {
                                    ...props.data,
                                    description: text
                                }
                            }))
                        }}
                    />
                </Card>
                <Card>
                    <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                        <Title level={5} style={{margin: 0}}>Elements</Title>
                        <Space>
                            <GenButton
                                style={{width: 130}}
                                type={'primary'}
                                loading={generating}
                                actions={[{
                                    key: "1", title: "Generate", action: async (model: string) => {
                                        await generateElements()
                                            .catch((e) => {
                                                console.log(e)
                                            })
                                    }
                                }]}
                            />

                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: [
                                        {
                                            key: "empty",
                                            label: <Space><FontAwesomeIcon icon={faPlus} style={{width: 16}}/>New empty
                                                element</Space>,
                                        },
                                        {
                                            key: "gen",
                                            label: <Space><BoltIcon secondary={faPlus} size={16}/>Generate new
                                                element</Space>,
                                        }
                                    ],
                                    onClick: async (e) => {
                                        if (e.key == 'empty') {
                                            await addEmptyElement()
                                        } else if (e.key == 'gen') {
                                            setGeneratingSingle(true)
                                            await generateElement().finally(() => {
                                                setGeneratingSingle(false)
                                            })

                                        }
                                    }
                                }}
                            >
                                <Button
                                    loading={generatingSingle}
                                    icon={<FontAwesomeIcon icon={faPlus}/>}
                                />
                            </Dropdown>
                        </Space>
                    </Flex>
                </Card>
                <Space direction={'vertical'}>
                    {props.data?.elements?.map((item, index) =>
                        <Card style={{flexGrow: 1}} key={index}
                              title={<Flex justify={'space-between'}>
                                  <Flex gap={10} style={{flexGrow: 1}}>
                                      <div style={{
                                          height: 30,
                                          minWidth: 30,
                                          width: 30,
                                          borderRadius: 30,
                                          background: "#ffffff",
                                          color: "#000",
                                          textAlign: "center",
                                          padding: 3
                                      }}>{index + 1}</div>
                                      <DebounceInput
                                          style={{fontWeight: 'bold', background: "#fff"}}
                                          placeholder={"Component title"}
                                          value={item.title}
                                          disabled={item.embedded}
                                          onChange={(text) => {
                                              dispatch(updateElement({
                                                  category: props.category.category ?? '',
                                                  componentId: props.data.id,
                                                  element: {
                                                      ...item,
                                                      title: text
                                                  }
                                              }))
                                          }}/>

                                      <Space>
                                          <Popover
                                              placement={'bottomRight'}
                                              content={
                                                  <Space direction={'vertical'}>
                                                      <TextArea onChange={(e) => setPrompt(e.target.value)}
                                                                value={prompt}
                                                                autoSize={true}
                                                                style={{width: 300}}
                                                      />
                                                      <Checkbox checked={useContext} onChange={(e) => {
                                                          setUseContext(e.target.checked)
                                                      }}>Include context</Checkbox>
                                                      <Button
                                                          type={'primary'}
                                                          onClick={async () => {
                                                              if (item.embedded) {
                                                                  setShowEmbeddingWarning(true)
                                                              } else {
                                                                  setLoading("element" + item.id)
                                                                  setOpen(undefined)
                                                                  await improveElement(item)
                                                                  setLoading(undefined)
                                                              }
                                                          }}>Go</Button>
                                                  </Space>
                                              }
                                              title="Prompt"
                                              trigger="click"
                                              open={open == "element-" + item.id}
                                              onOpenChange={(e) => {
                                                  setOpen(e ? "element-" + item.id : undefined)
                                              }}
                                          >
                                              <GenButton
                                                  mode={'icon'}
                                                  hideMainAction={true}
                                                  loading={loading == "element" + item.id}
                                                  open={open == undefined}
                                                  icon={<IconBoltGrad/>}
                                                  actions={[{
                                                      key: "1",
                                                      title: "Re-generate",
                                                      action: async (model: string) => {
                                                          if (item.embedded) {
                                                              setShowEmbeddingWarning(true)
                                                          } else {
                                                              await generateElement(item)
                                                                  .catch((e) => {
                                                                      console.log(e)
                                                                  })
                                                          }
                                                      },
                                                      render: (key, title, action) => {
                                                          return <Space><BoltIcon secondary={faRotate}
                                                                                  size={16}/>{title}</Space>
                                                      }
                                                  },
                                                      {
                                                          key: "2", title: "Improve", action: async (model: string) => {
                                                              if (item.embedded) {
                                                                  setShowEmbeddingWarning(true)
                                                              } else {
                                                                  await improveElement(item)
                                                              }
                                                          }, render: (key, title, action) => {
                                                              return <Flex gap={10} justify={'space-between'}>
                                                                  <Space><BoltIcon secondary={faArrowUpWideShort}
                                                                                   size={16}/>{title}</Space><Button
                                                                  type={'text'} size={'small'}
                                                                  icon={<FontAwesomeIcon icon={faPencil}/>}
                                                                  onClick={(e) => {
                                                                      setPrompt(categories.find(c => c.key == props.category.category)?.prompt.element.extend + '\n\nЭлемент: ' + item.title)
                                                                      e.stopPropagation()
                                                                      setOpen("element-" + item.id)
                                                                  }}/>
                                                              </Flex>
                                                          }
                                                      }]
                                                  }/>

                                          </Popover>
                                          {item.description && item.description?.length > 0 ?
                                              <EmbedButton
                                                  data={{
                                                      id: props.category.category + '-component' + props.data.id + '-element' + item.id,
                                                      text: item.description ?? '',
                                                      metadata:
                                                          {
                                                              title: item.title ?? '',
                                                              category: props.category.category ?? '',
                                                              type: 'setting'
                                                          },

                                                  }}
                                                  chunkSize={800}
                                                  chunkOverlap={100}
                                                  embedded={item.embedded}
                                                  edited={false}
                                                  onEmbedded={(data) => {
                                                      dispatch(updateElement({
                                                          category: props.category.category,
                                                          componentId: props.data.id,
                                                          element: {
                                                              ...item,
                                                              embedded: true
                                                          }
                                                      }))
                                                  }}
                                                  onUnembedded={(data) => {
                                                      dispatch(updateElement({
                                                          category: props.category.category,
                                                          componentId: props.data.id,
                                                          element: {
                                                              ...item,
                                                              embedded: false
                                                          }
                                                      }))
                                                  }}
                                              />
                                              : ''}
                                          <Button
                                              icon={<FontAwesomeIcon icon={faTrash}/>}
                                              disabled={item.embedded}
                                              style={{background: "#fff"}}
                                              onClick={() => {
                                                  dispatch(deleteElement({
                                                      // @ts-ignore
                                                      category: props.category.category ?? '',
                                                      componentId: props.data.id,
                                                      elementId: item.id
                                                  }))
                                              }}/>

                                      </Space>
                                  </Flex>
                              </Flex>}>

                            <TextAreaActions
                                style={{border: "none"}}
                                autoSize={true}
                                disabled={item.embedded}
                                value={item.description}
                                validate={text => true}
                                onChange={(text: string) => {
                                    dispatch(updateElement({
                                        category: props.category.category ?? '',
                                        componentId: props.data.id,
                                        element: {
                                            ...item,
                                            description: text
                                        }
                                    }))
                                }}
                            />
                        </Card>
                    )}
                </Space>
                {showEmbeddingWarning ? <EmbeddingErrorMessage
                    onClose={() => setShowEmbeddingWarning(false)}
                    content={<>
                        <div>You are trying to regenerate embedded elements:</div>
                        <ul>
                            {props.data.elements.filter(e => e.embedded).map(e => <li
                                style={{fontWeight: '700'}}>{e.title}</li>)}
                        </ul>
                        <div>You have to un-embed it before editing</div>
                    </>}
                    loading={unembedding}
                    onUnmbedClick={() => {
                        setUnembedding(true)
                        unembed({
                            project: projectId,
                            ids: props.data.elements.filter(e => e.embedded).map(e => props.category.category + '-component' + props.data.id + '-element' + e.id)
                        })
                            .then(() => {
                                dispatch(updateElements({
                                    category: props.category.category,
                                    componentId: props.data.id,
                                    elements: props.data.elements.filter(e => e.embedded).map(e => ({
                                        ...e,
                                        embedded: false
                                    }))
                                }))
                            }).finally(() => {
                            setUnembedding(false)
                            setShowEmbeddingWarning(false)
                        })
                    }}
                /> : ''}
                {showElementsWarning ? <Modal
                    open={true}
                    title={'Generation Warning'}
                    cancelText={'Cancel'}
                    okText={'Generate'}
                    onCancel={() => setShowElementsWarning(false)}
                    onOk={async () => {
                        setShowElementsWarning(false)
                        setGenerating(true)
                        await generateElements(true)
                        setGenerating(false)
                    }}
                >
                    <Space direction={'vertical'}>
                        <div style={{whiteSpace: 'pre-wrap'}}>You already have generated elements for this component.
                            <ul>
                                {props.data.elements.map(e => <li style={{fontWeight: 700}}>{e.title}</li>)}
                            </ul>
                            If you choose <strong>Generate</strong> option then existing data will be replaced with
                            newly
                            generated data. You will not be able to undone this action.
                        </div>
                    </Space>
                </Modal> : ''}
            </Flex>
        </>
    )
}